import { useMutation, useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";
import type { LegacyApiResponse } from "../types/api";
import { fetcher } from "./fetcher";

type QueryKeyPart = string | number | boolean | null | undefined;
type QueryKey = QueryKeyPart[];
const DEFAULT_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

type ApiQueryProps<TResponse, TBody = unknown> = {
  key: QueryKeyPart[];
  endPoint: string;
  method?: string;
  bodyContent?: TBody;
  body?: TBody;
  enabled?: boolean;
  dataIndex?: string;
  queryOptions?: Omit<
    UseQueryOptions<TResponse, Error, TResponse, QueryKey>,
    "queryKey" | "queryFn"
  >;
  dynamicKey?: QueryKeyPart;
};

type ApiMutationArgs<TBody = unknown> = {
  endPoint: string;
  method?: string;
  bodyContent?: TBody;
  body?: TBody;
  key?: QueryKeyPart[];
  dynamicKey?: QueryKeyPart;
  dataIndex?: string;
  enabled?: boolean;
};

export function useApiQuery<TResponse = LegacyApiResponse, TBody = unknown>(
  props: ApiQueryProps<TResponse, TBody>
) {
  const {
    key,
    endPoint,
    method = "GET",
    bodyContent,
    body,
    enabled,
    queryOptions = {},
    dynamicKey,
  } = props;
  const queryKey: QueryKey = dynamicKey
    ? [...key, dynamicKey]
    : [...key, endPoint];
  const requestBody = bodyContent ?? body;

  return useQuery<TResponse, Error, TResponse, QueryKey>({
    queryKey,
    queryFn: () =>
      fetcher<TResponse, TBody>(
        endPoint,
        method,
        method === "POST" ? requestBody : undefined
      ),
    enabled,
    ...DEFAULT_QUERY_OPTIONS,
    ...queryOptions,
  });
}

export function useApiMutation<
  TResponse = LegacyApiResponse,
  TBody = unknown,
>() {
  return useMutation<TResponse, Error, ApiMutationArgs<TBody>>({
    mutationFn: ({ endPoint, method = "POST", bodyContent, body }) =>
      fetcher<TResponse, TBody>(endPoint, method, bodyContent ?? body),
  });
}
